/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { CaseService } from 'app/cm/services/case-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { State } from 'app/shared/lib/state';
import { EventOptions, RuleActionOption, RuleConditionOption } from 'app/core/components/automations/automations-grid';
import { checkRuleActionOptionConfiguration, checkRuleConditionOptionConfiguration, RecordType } from 'app/core/components/automations/save-automation';
import { I18N } from 'aurelia-i18n';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortDirection, Sorting } from 'app/shared/lib/essette-site';

@autoinject()
export class ManageAutomationsPage {

    ////****************** Fields and Properties ************************************////
    protected moduleCode = 'CM';

    protected recordTypes: RecordType[] = [{ $display: 'Case', value: 'Case' }];
    protected options: RuleConditionOption[] = [];
    protected actions: RuleActionOption[] = [];
    protected actionsLoaded: boolean = false;

    private sorting = new Sorting();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private service: CaseService,
        private state: State,
        private i18n: I18N,
        private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    protected bind(): void {
        this.loadConditionOptions();
        this.loadActionOptions();
    }

    ////****************** Element References ***************************************////


    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadConditionOptions(): Promise<void> {
        try {
            this.state.beginLoad();

            this.sorting.entries = [
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];

            const options: Partial<IQueryLoadOptions> = {
                all: true,
                includeInactive: true,
                sorting: this.sorting
            }

            const [acuitiesResponse, programGroupsResponse, statusesResponse] = await Promise.all(
                [
                    this.service.loadAcuities(options),
                    this.service.loadProgramGroups(options, [], false, true, true),
                    this.service.loadCaseStatuses(options),
                ]
            );

            const acuities = acuitiesResponse.results;
            if(acuities.length) {
                this.options.push({
                    $used: false,
                    expression: '',
                    parsedExpression: null,
                    translatedDisplay: this.i18n.tr('cm:acuity.level.$long'),
                    options: acuities.map(a => {
                        return { ...a, $selected: false, $expanded: false }
                    }),
                    displayKey: 'cm:acuity.level.$long',
                    optionKey: 'cm:acuity.level.$long',
                    optionLevel1: 'acuity',
                    optionLevel2: 'code'
                });
            }
            const programGroups = programGroupsResponse.results;
            if(programGroups.length) {
                this.options.push({
                    $used: false,
                    expression: '',
                    parsedExpression: null,
                    translatedDisplay: this.i18n.tr('cm:programGroup.andProgram.$'),
                    options: programGroups.map(pg => {
                        return { ...pg, $selected: false, $expanded: false }
                    }),
                    displayKey: 'cm:programGroup.andProgram.$',
                    optionKey: 'cm:programGroup.$',
                    optionLevel1: 'program.group',
                    optionLevel2: 'code',
                    subOptionKey: 'cm:program.$',
                    subOptionProperty: 'programs',
                    subOptionLevel1: 'program',
                    subOptionLevel2: 'code'
                });
            }
            const statuses = statusesResponse.results;
            if(statuses.length) {
                this.options.push({
                    $used: false,
                    expression: '',
                    parsedExpression: null,
                    translatedDisplay: this.i18n.tr('cm:caseStatus.$long'),
                    options: statuses.map(s => {
                        return { ...s, $selected: false, $expanded: false }
                    }),
                    displayKey: 'cm:caseStatus.$long',
                    optionKey: 'cm:caseStatus.$long',
                    optionLevel1: 'status',
                    optionLevel2: 'code',
                    excludeOnEvent: EventOptions.Insert
                });
            }

            this.options.forEach(option => checkRuleConditionOptionConfiguration(option));
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadActionOptions(): Promise<void> {
        try {
            this.state.beginLoad();

            this.sorting.entries = [
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];

            const options: Partial<IQueryLoadOptions> = {
                all: true,
                includeInactive: true,
                sorting: this.sorting
            };

            const queuesResponse = await this.service.loadQueues(options);

            const queues = queuesResponse.results;
            const payloadLevel2 = 'code';
            this.actions.push({
                translatedDisplay: this.i18n.tr('ae:local.routeTo', { what: this.i18n.tr('cm:queue.$long') }),
                options: queues.map((o: any) => { return { ...o, payloadLevel2 } }),
                selectedOption: null,
                optionKey: 'cm:queue.$long',
                payloadLevel1: 'queues',
                payloadLevel2,
                successActionType: 'CM.Case.Route'
            });

            this.actions.forEach(action => checkRuleActionOptionConfiguration(action));
        } catch(error) {
            throw error;
        } finally {
            this.actionsLoaded = true;
            this.state.endLoad();
        }
    }

}
